import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, HourglassIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ExerciseItem } from "@/services/student/exercise.interfaces";
import {
  StudentExerciseProvider,
  useExerciseOptional,
} from "../adapter/StudentExerciseContext";

interface ExerciseTypeContainerProps {
  children: React.ReactNode;
  item?: Pick<ExerciseItem, "id" | "type"> & { moduleId?: string };
  moduleId?: string;
  title1: string;
  title2?: string;
  subtitle?: string;
  exerciseNumber: string;
  hideVerifyButton?: boolean;
  canVerify?: boolean;
  onVerify?: () => void;
  isVerifying?: boolean;
  isVerified?: boolean;
  isCorrect?: boolean;
}

const ExerciseTypeContainerChrome: React.FC<
  Omit<ExerciseTypeContainerProps, "item" | "moduleId">
> = ({
  children,
  title1,
  title2,
  subtitle,
  exerciseNumber,
  hideVerifyButton = false,
  canVerify = true,
  onVerify,
  isVerifying = false,
  isVerified = false,
  isCorrect = false,
}) => {
  const session = useExerciseOptional();
  const [showAnimation, setShowAnimation] = useState(false);

  const verifying = session?.status === "verifying" || isVerifying;
  const verified =
    session?.status === "verified" ||
    session?.status === "error" ||
    isVerified;
  const correct = session?.evaluation?.isCorrect ?? isCorrect;
  const allowVerify = session ? session.canVerify && canVerify : canVerify;

  useEffect(() => {
    if (verified && !verifying) {
      setShowAnimation(true);
      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [verified, verifying]);

  const handleVerifyClick = () => {
    if (onVerify) {
      onVerify();
      return;
    }
    if (session) {
      void Promise.resolve(
        session.runRegisteredVerify() ?? session.verify()
      ).catch(() => {});
    }
  };

  const cardClasses = cn(
    "w-full min-h-[336px] bg-white rounded-xl m-2 sm:m-4 shadow-md hover:shadow-lg transition-shadow duration-300 ease-in-out flex flex-col relative",
    {
      "border-l-8 border-l-green-500": verified && correct,
      "border-l-8 border-l-red-500": verified && !correct,
      "p-8": !verifying,
      "p-0 overflow-hidden": verifying,
      "animate-bounce-once": showAnimation && correct,
      "animate-shake": showAnimation && !correct,
    }
  );

  return (
    <div className={cardClasses}>
      {verifying && (
        <div className="absolute inset-0 bg-white flex flex-col items-center justify-center z-10">
          <div className="text-blue-500">
            <HourglassIcon className="h-16 w-16 animate-pulse mb-2" />
          </div>
          <div className="text-lg font-medium text-gray-700 mt-4">
            Verificando...
          </div>
        </div>
      )}

      {showAnimation && correct && (
        <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
          <div className="celebration-particles"></div>
        </div>
      )}

      <div className={cn("flex flex-col h-full", { "p-8": verifying })}>
        <div className="flex flex-col border-b border-gray-200 pb-4">
          <div className="flex items-baseline justify-between gap-4">
            <div className="text-sm italic text-gray-500">{title1}</div>
            <span
              className="shrink-0 text-[10px] text-gray-300 tabular-nums"
              title="ID do exercício (debug)"
            >
              {exerciseNumber}
            </span>
          </div>
          <div
            className="my-2.5"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, #e5e7eb 0, #e5e7eb 4px, transparent 4px, transparent 10px)",
              height: "1px",
            }}
          />
          {title2 && (
            <div className="text-zinc-900 text-xl font-semibold leading-snug">
              {title2}
            </div>
          )}
          {subtitle && (
            <div className="pt-2 text-sm text-gray-700">{subtitle}</div>
          )}
        </div>
        <div className="flex flex-col flex-grow pt-4">
          <div className="flex-grow">{children}</div>

          {verified && !verifying && (
            <div
              className={cn("mt-4 p-3 rounded-lg", {
                "bg-green-50 border border-green-200": correct,
                "bg-red-50 border border-red-200": !correct,
              })}
            >
              <div className="flex items-center font-medium">
                {correct ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <span>Correto!</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span>Incorreto!</span>
                  </>
                )}
              </div>
            </div>
          )}

          {!hideVerifyButton && !verifying && !verified && (
            <div className="flex justify-end mt-4">
              <button
                onClick={handleVerifyClick}
                disabled={!allowVerify}
                className={cn(
                  "inline-flex items-center gap-2 min-h-[44px] px-5 rounded-lg text-sm font-medium transition-all",
                  allowVerify
                    ? "bg-zinc-900 text-white hover:bg-zinc-700 active:scale-95"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                )}
              >
                <CheckCircle
                  className={cn("h-4 w-4", !allowVerify && "opacity-50")}
                />
                Verificar Resposta
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ExerciseTypeContainer: React.FC<ExerciseTypeContainerProps> = ({
  item,
  moduleId,
  ...chrome
}) => {
  if (!item) {
    return <ExerciseTypeContainerChrome {...chrome} />;
  }

  return (
    <StudentExerciseProvider item={item} moduleId={moduleId}>
      <ExerciseTypeContainerChrome {...chrome} />
    </StudentExerciseProvider>
  );
};
